import { Box, Button, Typography, TextField, MenuItem, Divider } from "@mui/material";
import { useEffect, useMemo } from "react";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useTranslate } from "../../Translate/TranslateContext";

const aspectOptions = [
  { label: "North", value: 0 },
  { label: "Northeast", value: 45 },
  { label: "East", value: 90 },
  { label: "Southeast", value: 135 },
  { label: "South", value: 180 },
  { label: "Southwest", value: 225 },
  { label: "West", value: 270 },
  { label: "Northwest", value: 315 },
];

function isValidNumber(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
}

function roundCoord(value) {
  return value === "" || value == null ? "" : Number(Number(value).toFixed(5));
}

function roundArea(value) {
  return Number(Number(value).toFixed(2));
}

function getGeometryTypeFromShape(shape) {
  if (Number(shape) === 0) return "circle";
  if (Number(shape) === 1) return "rectangle";
  if (Number(shape) === 2) return "polygon";
  return "circle";
}

function getRectangleCoordinates(rectBounds) {
  if (!Array.isArray(rectBounds) || rectBounds.length !== 2) return null;

  const [[latNW, lonNW], [latSE, lonSE]] = rectBounds;

  if (![latNW, lonNW, latSE, lonSE].every(isValidNumber)) return null;

  return [
    [Number(latNW), Number(lonNW)],
    [Number(latNW), Number(lonSE)],
    [Number(latSE), Number(lonSE)],
    [Number(latSE), Number(lonNW)],
  ];
}

function calculateCircleAreaHectares(geometry, fallbackArea) {
  const radius = Number(geometry?.radius_m);

  if (Number.isFinite(radius) && radius > 0) {
    return roundArea((Math.PI * radius * radius) / 10000);
  }

  if (isValidNumber(fallbackArea)) {
    return Number(fallbackArea);
  }

  return 1;
}

function calculatePolygonAreaHectares(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 3) return null;

  const validCoordinates = coordinates.filter(
    (point) =>
      Array.isArray(point) &&
      point.length >= 2 &&
      isValidNumber(point[0]) &&
      isValidNumber(point[1])
  );

  if (validCoordinates.length < 3) return null;

  const avgLat =
    validCoordinates.reduce((sum, [lat]) => sum + Number(lat), 0) /
    validCoordinates.length;

  const metersPerLat = 111320;
  const metersPerLon = 111320 * Math.cos((avgLat * Math.PI) / 180);

  const points = validCoordinates.map(([lat, lon]) => [
    Number(lon) * metersPerLon,
    Number(lat) * metersPerLat,
  ]);

  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i][0] * points[j][1] - points[j][0] * points[i][1];
  }

  return roundArea(Math.abs(area) / 2 / 10000);
}

export default function GardenLocationForm({ garden, setGarden, weatherStations = [], onNext }) {
  const { translate } = useTranslate();

  const update = (field, value) => {
    setGarden({ ...garden, [field]: value });
  };

  const geometryType = garden.geometry_type || getGeometryTypeFromShape(garden.garden_shape ?? 0);

  const rectangleCoordinates = useMemo(
    () => getRectangleCoordinates(garden.rect_bounds),
    [garden.rect_bounds]
  );

  const rectangleArea = useMemo(
    () => calculatePolygonAreaHectares(rectangleCoordinates),
    [rectangleCoordinates]
  );

  const polygonArea = useMemo(() => {
    if (geometryType !== "polygon") return null;
    return calculatePolygonAreaHectares(garden.geometry?.coordinates);
  }, [geometryType, garden.geometry]);

  const circleArea = useMemo(
    () => calculateCircleAreaHectares(garden.geometry, garden.garden_area_hectares),
    [garden.geometry, garden.garden_area_hectares]
  );

  const displayedArea =
    Number(garden.garden_shape) === 1
      ? rectangleArea ?? ""
      : Number(garden.garden_shape) === 2
      ? polygonArea ?? ""
      : circleArea;

  useEffect(() => {
    const nextGeometryType = getGeometryTypeFromShape(garden.garden_shape ?? 0);

    if (garden.geometry_type === nextGeometryType) return;

    setGarden({
      ...garden,
      geometry_type: nextGeometryType,
    });
  }, [garden.garden_shape]);

  useEffect(() => {
    if (Number(garden.garden_shape) !== 0) return;

    if (garden.garden_area_hectares == null || garden.garden_area_hectares === "") {
      setGarden({
        ...garden,
        geometry_type: "circle",
        garden_area_hectares: 1,
      });
    }
  }, [garden.garden_shape, garden.garden_area_hectares]);

  useEffect(() => {
    if (Number(garden.garden_shape) !== 1 || !rectangleCoordinates || rectangleArea == null) return;

    const nextGeometry = {
      type: "rectangle",
      coordinates: rectangleCoordinates,
    };

    const currentGeometryString = JSON.stringify(garden.geometry || {});
    const nextGeometryString = JSON.stringify(nextGeometry);

    if (
      garden.geometry_type === "rectangle" &&
      currentGeometryString === nextGeometryString &&
      Number(garden.garden_area_hectares) === rectangleArea
    ) {
      return;
    }

    setGarden({
      ...garden,
      geometry_type: "rectangle",
      geometry: nextGeometry,
      garden_area_hectares: rectangleArea,
    });
  }, [garden.garden_shape, garden.rect_bounds, rectangleArea]);

  useEffect(() => {
    if (Number(garden.garden_shape) !== 2 || polygonArea == null) return;

    if (
      garden.geometry_type === "polygon" &&
      Number(garden.garden_area_hectares) === polygonArea
    ) {
      return;
    }

    setGarden({
      ...garden,
      geometry_type: "polygon",
      garden_area_hectares: polygonArea,
    });
  }, [garden.garden_shape, polygonArea]);

  return (
    <Box sx={{ width: 360, display: "flex", flexDirection: "column", gap: 2, p: 2.5, borderRadius: "20px", background: "#FFFAF1", border: "1px solid #D4E9CF", boxShadow: "0 4px 14px rgba(0,0,0,0.04)" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#052812" }}>
        {translate("garden_location")}
      </Typography>

      <Divider />

      <TextField required select label={translate("garden_shape")} value={garden.garden_shape ?? 0} onChange={(e) => update("garden_shape", Number(e.target.value))}>
        <MenuItem value={0}>{translate("circle")}</MenuItem>
        <MenuItem value={1}>{translate("rectangle")}</MenuItem>
        <MenuItem value={2}>{translate("polygon")}</MenuItem>
      </TextField>

      <TextField required fullWidth label={translate("latitude")} type="number" value={garden.latitude_center ?? ""} onChange={(e) => update("latitude_center", e.target.value === "" ? null : roundCoord(e.target.value))} InputProps={{ startAdornment: <PlaceOutlinedIcon sx={{ mr: 1, color: "#24582A", fontSize: 20 }} /> }} />

      <TextField required fullWidth label={translate("longitude")} type="number" value={garden.longitude_center ?? ""} onChange={(e) => update("longitude_center", e.target.value === "" ? null : roundCoord(e.target.value))} InputProps={{ startAdornment: <PlaceOutlinedIcon sx={{ mr: 1, color: "#24582A", fontSize: 20 }} /> }} />

      <TextField required label={translate("garden_size_ha")} type="number" value={displayedArea} disabled={Number(garden.garden_shape) === 1 || Number(garden.garden_shape) === 2} onChange={(e) => update("garden_area_hectares", e.target.value === "" ? null : Number(e.target.value))} />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField required select fullWidth label={translate("slope_direction")} value={garden.aspect_degrees ?? 0} onChange={(e) => update("aspect_degrees", Number(e.target.value))}>
          {aspectOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField required sx={{ width: 100 }} label={translate("deg")} type="number" value={garden.slope_degrees ?? ""} onChange={(e) => update("slope_degrees", e.target.value === "" ? null : Number(e.target.value))} />
      </Box>

      <TextField required select label={translate("preferred_weather_station")} value={garden.preferred_ws_id ?? "open-meteo"} onChange={(e) => update("preferred_ws_id", e.target.value === "open-meteo" ? null : Number(e.target.value))} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320, borderRadius: "16px" } } } }}>
        <MenuItem value="open-meteo" sx={{ py: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CloudOutlinedIcon sx={{ fontSize: 18, color: "#24582A" }} />
            <Typography fontWeight={600}>Open Meteo</Typography>
          </Box>
        </MenuItem>

        <Divider />

        {weatherStations.map((ws) => (
          <MenuItem key={ws.ws_id} value={ws.ws_id} sx={{ py: 1 }}>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SensorsOutlinedIcon sx={{ fontSize: 18, color: "#24582A" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{`WS ${ws.ws_id}`}</Typography>
                </Box>

                {!!ws.distance && (
                  <Typography sx={{ fontSize: 11, color: "#5B6B61" }}>
                    {`${ws.distance.toFixed(1)} km`}
                  </Typography>
                )}
              </Box>

              <Typography sx={{ fontSize: 12, color: "#6A756E", ml: 3.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ws.location}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <Button variant="contained" onClick={onNext} sx={{ mt: 1, py: 1.2, borderRadius: "14px", textTransform: "none", fontWeight: 700, background: "#24582A", "&:hover": { background: "#17371A" } }}>
        {translate("continue")}
      </Button>
    </Box>
  );
}